import styled from "styled-components";

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
`;

/* ── Shared input primitives (reused across sections) ── */

export const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #dddddd;
  border-radius: 6px;
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  color: #333333;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: #aaaaaa;
  }

  &:focus {
    border-color: #000000;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #dddddd;
  border-radius: 6px;
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  color: #333333;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #000000;
  }
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 6px;
`;

export const FieldFull = styled.div`
  margin-bottom: 6px;
`;

export const SubmitButton = styled.button`
  margin-top: 10px;
  width: 100%;
  background-color: #000000;
  color: #ffffff;
  border: none;
  border-radius: 24px;
  padding: 10px 16px;
  font-family: "Aileron", sans-serif;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.2s ease;

  &:hover {
    background-color: #222222;
  }

  &:disabled {
    background-color: #888888;
    cursor: not-allowed;
  }
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  color: #555555;

  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    flex-shrink: 0;
  }
`;

/* ── Thank you state ── */

export const ThankYouWrapper = styled.div`
  background-color: #e5d8cb;
  padding: 160px 40px 80px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;

  @media (max-width: 768px) {
    padding: 120px 24px 64px;
  }
`;

export const ThankYouTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 8px 0;
`;

export const ThankYouSubtitle = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 15px;
  color: #444444;
  margin: 0 0 56px 0;
`;

export const BookingTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 34px;
  font-weight: 700;
  color: #000000;
  text-align: center;
  margin: 0 auto 32px;
  width: 100%;
`;

export const CalendlyBox = styled.div`
  background: #ffffff;
  border-radius: 20px;
  height: 900px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

export const CalendlyPlaceholderText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 14px;
  color: #aaaaaa;
`;

/* ── Hero section ── */

export const HeroSection = styled.section`
  background-color: #3487d3;
  padding: 120px 40px 80px;
  display: flex;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 100px 20px 64px;
  }
`;

export const HeroInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 56px;
  max-width: 1100px;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 40px;
  }
`;

export const HeroLeftSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const HeroHeading = styled.h1`
  font-family: "Poppins", sans-serif;
  font-size: 52px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 32px 0;
  line-height: 1.05;

  @media (max-width: 1024px) {
    font-size: 40px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const StatBox = styled.div`
  background-color: rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  padding: 14px 16px;
  backdrop-filter: blur(4px);
`;

export const StatNumber = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 3px;
`;

export const StatLabel = styled.div`
  font-family: "Aileron", sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.35;
`;

/* Hero right: collage + form card */

export const HeroRightSide = styled.div`
  position: relative;
  flex: 0 0 520px;
  height: 560px;

  @media (max-width: 1024px) {
    flex: 0 0 440px;
    height: 500px;
  }

  @media (max-width: 900px) {
    flex: none;
    width: 100%;
    max-width: 500px;
    height: 440px;
  }
`;

export const HeroCollage = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

export const CollageImg = styled.img<{ pos: "tl" | "tr" | "bl" | "br" }>`
  position: absolute;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);

  ${({ pos }) =>
    pos === "tl" &&
    `top: 0; left: 0; width: 50%; height: 48%;`}

  ${({ pos }) =>
    pos === "tr" &&
    `top: -24px; right: 0; width: 46%; height: 58%;`}

  ${({ pos }) =>
    pos === "bl" &&
    `bottom: 0; left: 4%; width: 44%; height: 50%;`}

  ${({ pos }) =>
    pos === "br" &&
    `bottom: -20px; right: 2%; width: 48%; height: 44%;`}
`;

export const FormCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #ffffff;
  border-radius: 20px;
  padding: 28px 26px;
  width: 310px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  z-index: 2;

  @media (max-width: 1024px) {
    width: 284px;
    padding: 22px 20px;
  }

  @media (max-width: 900px) {
    width: 310px;
  }
`;

export const FormTitle = styled.h3`
  font-family: "Poppins", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 2px 0;
`;

export const FormSubtitle = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 11px;
  color: #555555;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

export const SuccessMessage = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  text-align: center;
  margin-top: 10px;
`;

/* ── Engine section ── */

export const EngineSection = styled.section`
  background-color: #e5d8cb;
  padding: 52px 40px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 40px 24px;
  }
`;

export const EngineInner = styled.div`
  max-width: 1100px;
  width: 100%;
`;

export const EngineSectionHeading = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 32px 0;
  line-height: 1.2;
  max-width: 620px;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 24px;
  }
`;

export const FeaturesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const FeatureCard = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FeatureImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 14px;
  margin-bottom: 8px;
`;

export const FeatureTitle = styled.h3`
  font-family: "Poppins", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 6px 0;
`;

export const FeatureText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 14px;
  color: #555555;
  margin: 0;
  line-height: 1.6;
`;

/* ── Learn more section ── */

export const LearnMoreSection = styled.section`
  background-color: #c8defc;
  padding: 72px 40px;
  display: flex;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 56px 20px;
  }
`;

export const LearnMoreInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 56px;
  max-width: 1100px;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 40px;
  }
`;

export const LearnMoreLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 540px;
  }
`;

export const LearnMoreHeading = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 30px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 20px 0;
  line-height: 1.2;

  @media (max-width: 1024px) {
    font-size: 26px;
  }

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

export const LearnMoreText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 14px;
  color: #333333;
  margin: 0;
  line-height: 1.65;
`;

export const LearnMoreRightSide = styled.div`
  position: relative;
  flex: 0 0 520px;
  height: 480px;

  @media (max-width: 1024px) {
    flex: 0 0 440px;
    height: 420px;
  }

  @media (max-width: 900px) {
    flex: none;
    width: 100%;
    max-width: 500px;
    height: 400px;
  }
`;

/* ── Process section ── */

export const ProcessSection = styled.section`
  background-color: #f0ebe3;
  padding: 52px 40px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 40px 24px;
  }
`;

export const ProcessInner = styled.div`
  max-width: 1100px;
  width: 100%;
`;

export const ProcessTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 38px;
  font-weight: 700;
  color: #000000;
  text-align: center;
  margin: 0 0 32px 0;
`;

export const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const ProcessCard = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ProcessImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 14px;
  margin-bottom: 8px;
`;

export const ProcessCardTitle = styled.h3`
  font-family: "Poppins", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 6px 0;
`;

export const ProcessCardText = styled.p`
  font-family: "Aileron", sans-serif;
  font-size: 14px;
  color: #555555;
  margin: 0;
  line-height: 1.5;
`;

export const FooterBox = styled.div`
  width: 100%;
`;
